import { useState, useEffect } from 'react'
import { Step } from '../types'
import './HowItWorks.css'

const HowItWorks = () => {
  const [expandedImage, setExpandedImage] = useState<{ src: string; alt: string } | null>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedImage(null)
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [])

  const steps: (Step & { image?: string; imageAlt?: string; imageFrame?: string })[] = [
    {
      number: 1,
      icon: '📋',
      title: 'Gather',
      description:
        'Guided sections help you collect medical history, wishes, and important documents in one organized place.',
      image: '/gallery4.png',
      imageAlt: 'The Echo Difference — we listen, document, and create for you',
      imageFrame: 'portrait',
    },
    {
      number: 2,
      icon: '📦',
      title: 'Print & Protect',
      description:
        'Receive your personalized heirloom book and protective pouch—a tangible legacy of love and preparation.',
      image: '/gallery3.jpeg',
      imageAlt: 'Personalized Echo Legacy Book',
      imageFrame: 'portrait',
    },
    {
      number: 3,
      icon: '💝',
      title: 'Give Clarity',
      description:
        'When it matters most, your family has ONE place with everything they need. No searching. No confusion.',
      image: '/gallery1.png',
      imageAlt: 'Family sharing clarity and connection through their legacy book',
      imageFrame: 'portrait',
    },
  ]

  return (
    <section id="how-it-works" className="how-it-works">
      <div className="container">
        <div className="section-header">
          <span className="section-eyebrow">A Gentle Process</span>
          <h2>How It Works</h2>
          <p className="section-subtitle">Three calm steps toward peace of mind for the people you love</p>
        </div>

        <div className="steps-container">
          {steps.map((step) => (
            <div key={step.number} className="step">
              <div className="step-number">{step.number}</div>
              {step.image && (
                <div
                  className={`media-frame media-frame--${step.imageFrame} step-image`}
                  onClick={() => setExpandedImage({ src: step.image!, alt: step.imageAlt! })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    setExpandedImage({ src: step.image!, alt: step.imageAlt! })
                  }
                  aria-label={`View ${step.imageAlt}`}
                >
                  <img src={step.image} alt={step.imageAlt} className="step-img" />
                </div>
              )}
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>
      </div>

      {expandedImage && (
        <div
          className="step-lightbox"
          onClick={() => setExpandedImage(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setExpandedImage(null)}
          aria-label="Close"
        >
          <button
            className="step-lightbox-close"
            onClick={(e) => {
              e.stopPropagation()
              setExpandedImage(null)
            }}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={expandedImage.src}
            alt={expandedImage.alt}
            className="step-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  )
}

export default HowItWorks
